import React, { useEffect, useState } from "react";
import {
  Paper,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import agent from "../../app/api/agent";
import { Tenant } from "../../app/models/tenant";

export default function TenantSettingsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Tenant>({
    name: "",
    title: "",
    domain: "",
    minimumMembershipFee: 0,
    individualMembershipFee: 0,
    familyMembershipFee: 0,
    allowSepaPayments: false,
    street: "",
    city: "",
    zipCode: "",
    country: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    bankName: "",
    bankIBAN: "",
    bankBIC: "",
    donationBankName: "",
    donationIBAN: "",
    donationBIC: "",
  });

  useEffect(() => {
    agent.Tenants.getCurrent()
      .then((tenant) => {
        setFormData(tenant);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "minimumMembershipFee" ||
            name === "individualMembershipFee" ||
            name === "familyMembershipFee"
          ? Number(value)
          : value,
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await agent.Tenants.updateCurrent(formData);
      toast.success(t("tenantSettings.saveSuccess"));
    } catch {
      toast.error(t("tenantSettings.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const registrationLink = formData.id
    ? `${window.location.origin}/register-member?tenant=${formData.id}`
    : "";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(registrationLink);
    toast.success(t("tenantSettings.linkCopied"));
  };

  if (loading) return <CircularProgress />;

  return (
    <Paper sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>
        {t("tenantSettings.title")}
      </Typography>

      {registrationLink && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t("tenantSettings.registrationLink")}
          </Typography>
          <TextField
            value={registrationLink}
            fullWidth
            size="small"
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={t("tenantSettings.copyLink")}>
                      <IconButton onClick={handleCopyLink} size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {t("tenantSettings.registrationLinkHelp")}
          </Typography>
        </Box>
      )}

      <Box component="form" noValidate autoComplete="off">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={t("tenantSettings.organizationName")}
              name="name"
              value={formData.name || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={t("tenantSettings.displayTitle")}
              name="title"
              value={formData.title || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label={t("tenantSettings.domain")}
              name="domain"
              value={formData.domain || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t("tenantSettings.fees")}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.minimumFee")}
              name="minimumMembershipFee"
              type="number"
              value={formData.minimumMembershipFee ?? 0}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.individualFee")}
              name="individualMembershipFee"
              type="number"
              value={formData.individualMembershipFee ?? 0}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.familyFee")}
              name="familyMembershipFee"
              type="number"
              value={formData.familyMembershipFee ?? 0}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allowSepaPayments || false}
                  onChange={handleInputChange}
                  name="allowSepaPayments"
                />
              }
              label={t("tenantSettings.allowSepa")}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t("tenantSettings.address")}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              label={t("tenantSettings.street")}
              name="street"
              value={formData.street || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.zipCode")}
              name="zipCode"
              value={formData.zipCode || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={t("tenantSettings.city")}
              name="city"
              value={formData.city || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={t("tenantSettings.country")}
              name="country"
              value={formData.country || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t("tenantSettings.contact")}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.contactName")}
              name="contactName"
              value={formData.contactName || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.contactEmail")}
              name="contactEmail"
              value={formData.contactEmail || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.contactPhone")}
              name="contactPhone"
              value={formData.contactPhone || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t("tenantSettings.bankDetails")}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.bankName")}
              name="bankName"
              value={formData.bankName || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.bankIBAN")}
              name="bankIBAN"
              value={formData.bankIBAN || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.bankBIC")}
              name="bankBIC"
              value={formData.bankBIC || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t("tenantSettings.donationAccount")}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.bankName")}
              name="donationBankName"
              value={formData.donationBankName || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.bankIBAN")}
              name="donationIBAN"
              value={formData.donationIBAN || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label={t("tenantSettings.bankBIC")}
              name="donationBIC"
              value={formData.donationBIC || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ textAlign: "right", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : t("tenantSettings.save")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
